/* KK home layer — slideshow, tabs, rows, size chips, quick add-to-cart, reveal.
   Add to cart talks to Horizon's own cart drawer through its `cart:update` event,
   so the drawer, cart bubble and Shiprocket/Fastrr checkout button keep working unchanged. */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Hero slideshow (scroll-snap + autoplay) ---------- */
function initSlider(root) {
  if (root.dataset.kkReady) return;
  root.dataset.kkReady = '1';
  const track = root.querySelector('[data-kk-track]');
  const slides = [...root.querySelectorAll('[data-kk-slide]')];
  const dots = [...root.querySelectorAll('[data-kk-dot]')];
  if (!track || slides.length < 2) return;

  let index = 0;
  let timer = null;
  const delay = Number(root.dataset.autoplay || 0) * 1000;

  const go = (i, smooth = true) => {
    index = (i + slides.length) % slides.length;
    track.scrollTo({ left: slides[index].offsetLeft, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
  };
  const paint = () => dots.forEach((d, i) => d.classList.toggle('is-active', i === index));

  track.addEventListener('scroll', () => {
    const i = Math.round(track.scrollLeft / track.clientWidth);
    if (i !== index) { index = i; paint(); }
  }, { passive: true });

  root.querySelector('[data-kk-prev]')?.addEventListener('click', () => { go(index - 1); restart(); });
  root.querySelector('[data-kk-next]')?.addEventListener('click', () => { go(index + 1); restart(); });
  dots.forEach((d) => d.addEventListener('click', () => { go(Number(d.dataset.kkDot)); restart(); }));

  const stop = () => { clearInterval(timer); timer = null; };
  const start = () => {
    if (!delay || reduceMotion || timer) return;
    timer = setInterval(() => { if (!document.hidden) go(index + 1); }, delay);
  };
  const restart = () => { stop(); start(); };
  root.addEventListener('pointerenter', stop);
  root.addEventListener('pointerleave', start);
  track.addEventListener('touchstart', stop, { passive: true });
  track.addEventListener('touchend', () => setTimeout(start, 4000), { passive: true });

  paint();
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}

/* ---------- Tabs ---------- */
function initTabs(root) {
  if (root.dataset.kkTabsReady) return;
  root.dataset.kkTabsReady = '1';
  const tabs = [...root.querySelectorAll('[data-kk-tab]')];
  const panels = [...root.querySelectorAll('[data-kk-panel]')];
  tabs.forEach((tab) => tab.addEventListener('click', () => {
    tabs.forEach((t) => { const on = t === tab; t.classList.toggle('is-active', on); t.setAttribute('aria-selected', on); });
    panels.forEach((p) => {
      const on = p.dataset.kkPanel === tab.dataset.kkTab;
      p.hidden = !on;
      p.classList.toggle('is-active', on);
      if (on) p.querySelectorAll('[data-kk-row]').forEach(updateRow);
    });
    tab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
  }));
}

/* ---------- Swipe rows with arrows below ---------- */
function updateRow(row) {
  const track = row.querySelector('[data-kk-row-track]');
  const arrows = row.querySelector('.kk-arrows');
  if (!track || !arrows) return;
  const max = track.scrollWidth - track.clientWidth;
  arrows.hidden = max < 4;
  row.querySelector('[data-kk-row-prev]').disabled = track.scrollLeft < 4;
  row.querySelector('[data-kk-row-next]').disabled = track.scrollLeft > max - 4;
}
function initRow(row) {
  if (row.dataset.kkRowReady) return;
  row.dataset.kkRowReady = '1';
  const track = row.querySelector('[data-kk-row-track]');
  if (!track) return;
  const step = (dir) => {
    const card = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const w = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
    const per = Math.max(1, Math.floor((track.clientWidth + gap) / w));
    track.scrollBy({ left: dir * w * per, behavior: reduceMotion ? 'auto' : 'smooth' });
  };
  row.querySelector('[data-kk-row-prev]')?.addEventListener('click', () => step(-1));
  row.querySelector('[data-kk-row-next]')?.addEventListener('click', () => step(1));
  track.addEventListener('scroll', () => updateRow(row), { passive: true });
  new ResizeObserver(() => updateRow(row)).observe(track);
  updateRow(row);
}

/* ---------- Size chips ---------- */
document.addEventListener('click', (e) => {
  const chip = e.target.closest('[data-kk-chip]');
  if (!chip || chip.disabled) return;
  const group = chip.closest('.kk-chips');
  group.querySelectorAll('[data-kk-chip]').forEach((c) => c.setAttribute('aria-checked', c === chip));
  group.classList.remove('is-nudge');
  const btn = chip.closest('[data-kk-card]')?.querySelector('[data-kk-add]');
  if (btn) { btn.dataset.variantId = chip.dataset.variantId; btn.textContent = btn.dataset.label; }
});

/* ---------- Quick add to cart (Horizon cart drawer) ---------- */
const root = () => (window.Shopify?.routes?.root || '/');

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId);
}

async function addToCart(btn) {
  const card = btn.closest('[data-kk-card]');
  const id = btn.dataset.variantId;
  if (!id) {
    const chips = card?.querySelector('.kk-chips');
    if (chips) {
      chips.classList.remove('is-nudge'); void chips.offsetWidth; chips.classList.add('is-nudge');
      btn.textContent = btn.dataset.pick || 'Select an option';
    }
    return;
  }

  card?.querySelector('.kk-card__error')?.remove();
  window.KKDrawer?.celebrate?.();           // fireworks + free-gift card; the drawer opens after it
  btn.classList.add('is-busy');
  btn.textContent = 'Adding…';
  const sections = cartSectionIds();

  try {
    const res = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], ...(sections.length ? { sections: sections.join(',') } : {}) }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');

    const cart = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

    // Same event shape Horizon's product form dispatches (CartAddEvent → 'cart:update').
    const ev = new Event('cart:update', { bubbles: true });
    ev.detail = {
      resource: cart,
      sourceId: `kk-${id}`,
      data: { source: 'kk-quick-add', itemCount: cart.item_count, productId: card?.dataset.productId, variantId: id, sections: data.sections },
    };
    document.dispatchEvent(ev);

    btn.classList.remove('is-busy');
    btn.classList.add('is-done');
    btn.textContent = 'Added ✓';
    setTimeout(() => { btn.classList.remove('is-done'); btn.textContent = btn.dataset.label || 'Add to cart'; }, 2200);

    const drawer = document.querySelector('cart-drawer-component');
    if (window.KKDrawer) {
      // our drawer (waits for the celebration if it is showing); on Horizon 4 the component has no open()
      setTimeout(() => window.KKDrawer.open(), 250);
    } else if (drawer && typeof drawer.open === 'function') {
      if (!drawer.hasAttribute('auto-open')) setTimeout(() => drawer.open(), 250);
    } else {
      window.location.href = `${root()}cart`;
    }
  } catch (err) {
    window.KKDrawer?.cancelCelebrate?.();
    btn.classList.remove('is-busy');
    btn.textContent = btn.dataset.label || 'Add to cart';
    const msg = document.createElement('p');
    msg.className = 'kk-card__error';
    msg.setAttribute('role', 'alert');
    msg.textContent = err.message;
    btn.after(msg);
    setTimeout(() => msg.remove(), 5000);
  }
}

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-kk-add]');
  if (btn && btn.tagName === 'BUTTON') { e.preventDefault(); addToCart(btn); }
});

/* ---------- Reveal on scroll ---------- */
const io = !reduceMotion && 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
    }), { rootMargin: '0px 0px -8% 0px' })
  : null;

function initReveal(scope) {
  if (!io || window.Shopify?.designMode) return;
  scope.querySelectorAll('.kk-head, .kk-cats__item, .kk-grid:not(.kk-list__grid) > .kk-card, .kk-spot__tile, .kk-budget__grid > li, .kk-story__media, .kk-story__copy')
    .forEach((el, i) => {
      if (el.dataset.kkRevealed) return;
      el.dataset.kkRevealed = '1';
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight) return; // already on screen: never hide it
      el.classList.add('kk-reveal');
      el.style.transitionDelay = `${(i % 4) * 70}ms`;
      io.observe(el);
    });
}

/* ---------- Reels: play muted while on screen, tap the speaker for sound ---------- */
const reelIO = "IntersectionObserver" in window
  ? new IntersectionObserver((entries) => entries.forEach((en) => {
      const v = en.target;
      if (en.isIntersecting && !reduceMotion) v.play().catch(() => {});
      else v.pause();
    }), { threshold: 0.6 })
  : null;

function initReels(scope) {
  scope.querySelectorAll("[data-kk-reel] video").forEach((v) => {
    if (v.dataset.kkReady) return;
    v.dataset.kkReady = "1";
    v.muted = true;
    v.setAttribute("playsinline", "");
    reelIO?.observe(v);
  });
}

document.addEventListener("click", (e) => {
  const btn = e.target.closest("[data-kk-sound]");
  if (!btn) return;
  const video = btn.closest("[data-kk-reel]")?.querySelector("video");
  if (!video) return;
  const turnOn = video.muted;
  document.querySelectorAll("[data-kk-reel] video").forEach((v) => { v.muted = true; });
  document.querySelectorAll("[data-kk-sound]").forEach((b) => { b.setAttribute("aria-pressed", "false"); b.setAttribute("aria-label", "Turn sound on"); });
  if (turnOn) {
    video.muted = false;
    video.play().catch(() => {});
    btn.setAttribute("aria-pressed", "true");
    btn.setAttribute("aria-label", "Turn sound off");
  }
});

/* ---------- Count-up numbers (budget cards) ---------- */
const countIO = "IntersectionObserver" in window
  ? new IntersectionObserver((entries) => entries.forEach((en) => {
      if (!en.isIntersecting) return;
      countIO.unobserve(en.target);
      const el = en.target, end = Number(el.dataset.kkCount), pre = el.dataset.prefix || "", suf = el.dataset.suffix || "";
      const fmt = (n) => pre + new Intl.NumberFormat("en-IN").format(n) + suf;
      const t0 = performance.now(), dur = 1300;
      const tick = (t) => {
        const k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 3);
        el.textContent = fmt(Math.round(end * e));
        if (k < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }), { threshold: 0.4 })
  : null;

function initCounts(scope) {
  if (!countIO || reduceMotion) return;
  scope.querySelectorAll("[data-kk-count]").forEach((el) => {
    if (el.dataset.kkCountReady) return;
    el.dataset.kkCountReady = "1";
    el.textContent = (el.dataset.prefix || "") + "0" + (el.dataset.suffix || "");
    countIO.observe(el);
  });
}

/* ---------- Drag to scroll (category circles) ---------- */
function initDrag(scope) {
  scope.querySelectorAll("[data-kk-drag]").forEach((track) => {
    if (track.dataset.kkDragReady) return;
    track.dataset.kkDragReady = "1";
    let down = false, startX = 0, startLeft = 0, moved = false;
    track.addEventListener("pointerdown", (e) => {
      if (e.pointerType !== "mouse") return;
      down = true; moved = false; startX = e.clientX; startLeft = track.scrollLeft;
    });
    window.addEventListener("pointermove", (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 5) { moved = true; track.classList.add("is-dragging"); }
      track.scrollLeft = startLeft - dx;
    });
    window.addEventListener("pointerup", () => {
      if (!down) return;
      down = false;
      setTimeout(() => track.classList.remove("is-dragging"), 0);
    });
    track.addEventListener("click", (e) => { if (moved) { e.preventDefault(); moved = false; } }, true);
  });
}

/* ---------- Boot (and re-boot inside the theme editor) ---------- */
function boot(scope = document) {
  scope.querySelectorAll('[data-kk-slider]').forEach(initSlider);
  scope.querySelectorAll('[data-kk-tabs]').forEach(initTabs);
  scope.querySelectorAll('[data-kk-row]').forEach(initRow);
  initReels(scope);
  initCounts(scope);
  initDrag(scope);
  initReveal(scope);
}
boot();
document.addEventListener('shopify:section:load', (e) => boot(e.target));
document.addEventListener('shopify:block:select', (e) => {
  const slide = e.target.closest('[data-kk-slide]');
  const slider = slide?.closest('[data-kk-slider]');
  if (slider) slider.querySelector('[data-kk-track]').scrollTo({ left: slide.offsetLeft });
  const tabsRoot = e.target.closest('[data-kk-tabs]');
  if (tabsRoot && e.target.matches('[data-kk-panel]')) tabsRoot.querySelector(`[data-kk-tab="${e.target.dataset.kkPanel}"]`)?.click();
});
